import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../lib/axios';
import MinutesToArrivalComponent from './MinutesToArrivalComponent';
import TrackingStatusBar from './TrackingStatusBar';
import { FocusContext, useFocusable } from '@noriginmedia/norigin-spatial-navigation';
import TransparentButton from '../../components/Buttons/TransparentButton';
import OrderDetailsPopup from './OrderDetailsPopup';
import {
    OrderModel,
    OrderStatusObject,
    setLastOrderStatus,
    trackLastOrder,
} from '../../redux/slices/ordersSlice';
import { getAccessToken } from '../../lib/auth';
import { setAppBackground } from '../../redux/slices/layoutSlice';
import { useDispatch } from 'react-redux';
import createTranslateFunction from '../../i18n/createTranslateFunction';
import DidYouGetTheOrderPopup from './DidYouGetTheOrderPopup';
import { AppDispatch, useSelector } from '../../redux/store';
import { getRemainingMinutes } from './TrackOrderFunctions';
import TimeoutPopup from './TimeoutPopup';

type TrackOrderScreenProps = {
    focusKey: string;
};
const TrackOrderScreen = ({ focusKey: focusKeyParam }: TrackOrderScreenProps) => {
    const trackOrderTranslateFunction = createTranslateFunction('buttons');
    const { orderId } = useParams();
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const [isOrderDetailsOpen, setIsOrderDetailsOpen] = useState<boolean>(false);
    const [orderData, setOrderData] = useState<OrderModel>({} as OrderModel);
    const [estimatedTimeOfArrival, setEstimatedTimeOfArrival] = useState<number>(50);
    const [isTimeoutPopupOpen, setIsTimeoutPopupOpen] = useState<boolean>(false);
    const { last_order_status, last_order } = useSelector((state) => state.orders);
    const { ref, focusKey, focusSelf } = useFocusable({
        focusKey: focusKeyParam,
        preferredChildFocusKey: 'ORDER-DETAILS-BUTTON',
    });
    const isOrderDelivery = (last_order && last_order.last_order[0].type === 0) || false;

    const fetchOrderData = async () => {
        try {
            const { data } = await api.get(`/order/get_order_data/${orderId}`, {
                headers: {
                    Authorization: `Bearer ${getAccessToken()?.access_token}`,
                },
            });
            setOrderData(data);
        } catch (error) {
            console.error('Error fetching order data:', error);
        }
    };

    useEffect(() => {
        focusSelf();
        fetchOrderData();
        dispatch(trackLastOrder());
        dispatch(
            setAppBackground([
                'https://pizzahut-assets.s3.amazonaws.com/screen-bg/screens/tracking-page/Track_Order_Background.png',
                '',
            ]),
        );

        // Set up timeout popup
        const timeoutId = setTimeout(() => {
            console.log('timeout');
            setIsTimeoutPopupOpen(true);
        }, 1200000); // 20 minutes

        return () => {
            if (last_order_status === 4) {
                localStorage.removeItem('TrackLastOrder');
            }
            clearTimeout(timeoutId);
        };
    }, []);

    // Set up polling for both order data and tracking status
    useEffect(() => {
        let intervalId: NodeJS.Timeout | null = null;

        if (!isTimeoutPopupOpen) {
            intervalId = setInterval(() => {
                // Fetch both order data and tracking status
                fetchOrderData();
                dispatch(trackLastOrder());
            }, 180000); // 3 minutes
        }

        return () => {
            if (intervalId) {
                clearInterval(intervalId);
            }
        };
    }, [dispatch, isTimeoutPopupOpen]);

    useEffect(() => {
        const futureDate = last_order?.last_order[0].done_date;
        if (futureDate) {
            setEstimatedTimeOfArrival(getRemainingMinutes(futureDate));
        }
    }, [last_order]);

    useEffect(() => {
        if (last_order_status === 4) {
            localStorage.removeItem('TrackLastOrder');
            return;
        }

        const TrackLastOrderObject = {
            orderId: orderId,
            orderStatus: OrderStatusObject[last_order_status],
        };
        localStorage.setItem('TrackLastOrder', JSON.stringify(TrackLastOrderObject));
    }, [last_order_status]);

    const toggleBetweenOrderDetails = (action: 'plus' | 'minus') => {
        if (action === 'plus' && last_order_status < 4) {
            dispatch(setLastOrderStatus(last_order_status + 1));
        } else if (action === 'minus' && last_order_status > 0) {
            dispatch(setLastOrderStatus(last_order_status - 1));
        }
    };

    return (
        <FocusContext.Provider value={focusKey}>
            <div ref={ref} className="relative w-full h-full flex justify-evenly">
                {/* //TODO: remove this
                <div className="absolute top-[3vw] left-[5vw] flex flex-col justify-center items-center">
                    <span className="text-[1.6vw]">{`Toggle Between Order Statuses!`}</span>
                    <TransparentButton
                        className="text-[1.6vw] w-[4vw]"
                        onClick={() => toggleBetweenOrderDetails('plus')}
                    >{`+`}</TransparentButton>
                    <TransparentButton
                        className="text-[1.6vw] w-[4vw]"
                        onClick={() => toggleBetweenOrderDetails('minus')}
                    >{`-`}</TransparentButton>
                    <TransparentButton
                        className="text-[1.6vw] w-[10vw]"
                        onClick={() => dispatch(trackLastOrder())}
                    >{`Track Last Order Button`}</TransparentButton>
                </div>
                 */}
                <TransparentButton
                    className="w-[10vw] absolute top-[3vw] right-[3vw]"
                    onClick={() => navigate('/')}
                    focusKey="RETURN-HOME-PAGE-BUTTON"
                >
                    {trackOrderTranslateFunction('back_to_home')}
                </TransparentButton>
                <TransparentButton
                    className="w-[10vw] absolute top-[3vw] right-[15vw]"
                    focusKey="ORDER-DETAILS-BUTTON"
                    onClick={() => setIsOrderDetailsOpen(!isOrderDetailsOpen)}
                >
                    {trackOrderTranslateFunction('order_details')}
                </TransparentButton>
                <div className="flex pt-[8vw]">
                    <TrackingStatusBar isOrderDelivery={isOrderDelivery} />
                    <MinutesToArrivalComponent MinutesToArrival={estimatedTimeOfArrival} />
                </div>
                {isOrderDetailsOpen && orderData._id && (
                    <OrderDetailsPopup orderData={orderData} focusKey="ORDER-DETAILS-POPUP" />
                )}
                {(estimatedTimeOfArrival < 1 || last_order_status >= 4) && (
                    <DidYouGetTheOrderPopup />
                )}
                <TimeoutPopup
                    opened={isTimeoutPopupOpen}
                    close={() => setIsTimeoutPopupOpen(false)}
                    focusKey="TIMEOUT-POPUP"
                />
            </div>
        </FocusContext.Provider>
    );
};

export default TrackOrderScreen;
